import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, ZoomControl, useMapEvents } from "react-leaflet";
import { AlertTriangle, RotateCw, MousePointerClick, ListOrdered, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BaseTiles, ParcelShape, TURKEY_CENTER } from "@/components/LocationPicker";
import { ParcelSearchForm } from "@/components/parcel/ParcelSearchForm";
import { ParcelInfoCard, parcelLabel } from "@/components/parcel/ParcelInfoCard";
import { PortfolioForm } from "@/components/portfolio/PortfolioForm";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const MapClick = ({ active, onPick }) => {
  useMapEvents({ click: (e) => active && onPick(e.latlng) });
  return null;
};

const toPortfolio = (p) => ({
  title: `${p.mahalle} ${p.nitelik || "Parsel"} · Ada ${p.ada} Parsel ${p.parsel}`.slice(0, 120),
  property_type: "ARSA", rooms: "ARSA", area_m2: p.alan ? String(Math.round(p.alan * 100) / 100) : "",
  city: p.il || "", district: p.ilce || "", neighborhood: p.mahalle || "", lat: p.lat, lng: p.lng,
  description: `TKGM parsel bilgisi: ${parcelLabel(p)}${p.nitelik ? ` · Nitelik: ${p.nitelik}` : ""}${p.alan_text ? ` · Yüzölçümü: ${p.alan_text} m²` : ""}`,
  parcel: { il: p.il, ilce: p.ilce, mahalle: p.mahalle, mahalle_id: p.mahalle_id, ada: p.ada, parsel: p.parsel, alan: p.alan,
    alan_text: p.alan_text, nitelik: p.nitelik, mevkii: p.mevkii, pafta: p.pafta, geometry: p.geometry },
});

export default function ParcelPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("form");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);

  const run = async (url, params) => {
    setBusy(true); setError(null);
    try { setResult((await api.get(url, { params })).data); }
    catch (x) { setResult(null); setError({ msg: formatApiError(x.response?.data?.detail), status: x.response?.status, retry: () => run(url, params) }); }
    setBusy(false);
  };
  const listError = (x) => setError({ msg: formatApiError(x.response?.data?.detail), status: x.response?.status, retry: () => window.location.reload() });

  return (
    <div className="space-y-6" data-testid="parcel-page">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">TKGM</div>
        <h1 className="text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Parsel Sorgu</h1>
        <p className="text-slate-500 mt-1">Ada/parsel veya haritaya tıklayarak resmi parsel sınırını görün, tek tıkla portföye dönüştürün.</p>
      </div>
      <div className="grid lg:grid-cols-[360px_1fr] gap-5">
        <Card className="p-5 space-y-5 h-fit">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="w-full">
              <TabsTrigger value="form" className="flex-1" data-testid="parcel-tab-form"><ListOrdered className="w-3.5 h-3.5 mr-1.5" />Ada / Parsel</TabsTrigger>
              <TabsTrigger value="map" className="flex-1" data-testid="parcel-tab-map"><MousePointerClick className="w-3.5 h-3.5 mr-1.5" />Haritadan seç</TabsTrigger>
            </TabsList>
            <TabsContent value="form" forceMount className="data-[state=inactive]:hidden pt-3"><ParcelSearchForm busy={busy} onError={listError} onSearch={(q) => run("/parcels/query", q)} /></TabsContent>
            <TabsContent value="map" className="pt-3 text-sm text-slate-600" data-testid="parcel-map-hint">Haritayı yakınlaştırıp parselin üzerine tıklayın. Sınırlar TKGM kayıtlarından çizilir.</TabsContent>
          </Tabs>
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-2" data-testid="parcel-error">
              <div className="flex gap-2 text-sm text-red-800"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{error.msg}</div>
              {error.status !== 404 && <Button size="sm" variant="outline" data-testid="parcel-retry-btn" onClick={error.retry}><RotateCw className="w-3.5 h-3.5 mr-1.5" />Tekrar dene</Button>}
            </div>
          )}
          {result && <ParcelInfoCard result={result} canConvert={!!user.office_id} onConvert={() => setForm(toPortfolio(result.parcel))} />}
        </Card>
        <Card className="overflow-hidden relative z-0">
          <div className={`h-[calc(100vh-240px)] min-h-[460px] ${tab === "map" ? "cursor-crosshair" : ""}`} data-testid="parcel-map">
            <MapContainer center={TURKEY_CENTER} zoom={6} className="h-full w-full" zoomControl={false}>
              <ZoomControl position="topright" />
              <BaseTiles />
              <MapClick active={tab === "map" && !busy} onPick={({ lat, lng }) => run("/parcels/at", { lat: lat.toFixed(6), lng: lng.toFixed(6) })} />
              <ParcelShape geometry={result?.parcel.geometry} />
            </MapContainer>
          </div>
          {busy && <div className="absolute top-3 left-3 z-[500] bg-white/90 rounded-full px-3 py-1.5 text-xs flex items-center gap-1.5 shadow"><Loader2 className="w-3.5 h-3.5 animate-spin" />TKGM sorgulanıyor…</div>}
        </Card>
      </div>
      <PortfolioForm open={!!form} initial={form} onClose={() => setForm(null)}
        onSaved={() => navigate("/portfolios")} />
    </div>
  );
}
